/** ISO-8601 timestamp in UTC (same as `new Date().toISOString()`). */
export function utcNowIso(): string {
  return new Date().toISOString();
}

/** Format an ISO string for display in UTC. */
export function formatUtcDateTime(iso: string, options?: Intl.DateTimeFormatOptions): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  const formatted = date.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...options,
  });

  return `${formatted} UTC`;
}

/** Format an ISO string as a UTC calendar date, no time (`Jan 1, 2027`). */
export function formatUtcDate(iso: string): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return date.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Chat history row: `01 Jun, 2026 11:47 AM` (UTC). */
export function formatUtcChatListUpdatedAt(iso: string): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  const opts: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };
  const day = new Intl.DateTimeFormat('en-US', { ...opts, day: '2-digit' }).format(date);
  const month = new Intl.DateTimeFormat('en-US', { ...opts, month: 'short' }).format(date);
  const year = new Intl.DateTimeFormat('en-US', { ...opts, year: 'numeric' }).format(date);
  const time = new Intl.DateTimeFormat('en-US', {
    ...opts,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  return `${day} ${month}, ${year} ${time}`;
}

/** Human-readable relative time; falls back to UTC datetime for older items. */
export function formatUtcRelativeDateTime(iso: string): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) {
    return 'Just now';
  }
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr} hr ago`;
  }
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }

  return formatUtcDateTime(iso);
}
